import {
  MAX_FILE_SIZE_MB,
  SUPPORTED_MIME_TYPES,
  TABULAR_FILE_EXTENSIONS,
  TABULAR_MIME_TYPES,
} from "@f2e/shared/constants"
import { normalizeMarkdownType } from "@f2e/shared/browser"

import { UploadingFile } from "@/components/Conversation/FileUploadTypes"

// Re-export for backward compatibility
export { MAX_FILE_SIZE_MB, normalizeMarkdownType }
export const MAX_KB_UPLOAD_FILES = 50
export const SUPPORTED_KB_MIME_TYPES = SUPPORTED_MIME_TYPES

export interface TabularUploadCapabilities {
  enabled: boolean
  accepted_extensions: string[]
  accepted_mime_types: string[]
  max_source_bytes: number
}

export type TabularFileValidationFailure = "format" | "size"

const TABULAR_MIME_BY_EXTENSION: Record<string, string> = {
  ".csv": TABULAR_MIME_TYPES[0],
  ".xlsx": TABULAR_MIME_TYPES[1],
}

export const getTabularFileExtension = (name: string): string | null => {
  const normalized = name.trim().toLowerCase()
  return (
    TABULAR_FILE_EXTENSIONS.find((extension) =>
      normalized.endsWith(extension)
    ) ?? null
  )
}

export const getTabularFileValidationFailure = (
  file: File,
  capabilities: TabularUploadCapabilities
): TabularFileValidationFailure | null => {
  const extension = getTabularFileExtension(file.name)
  const looksTabular =
    extension !== null || capabilities.accepted_mime_types.includes(file.type)
  if (!looksTabular) return null

  if (
    !capabilities.enabled ||
    !extension ||
    !capabilities.accepted_extensions.includes(extension) ||
    file.type !== TABULAR_MIME_BY_EXTENSION[extension] ||
    !capabilities.accepted_mime_types.includes(file.type)
  ) {
    return "format"
  }
  if (file.size > capabilities.max_source_bytes) return "size"
  return null
}

/**
 * Checks if a file type is supported based on MIME type
 * @param file The file to check
 * @returns boolean indicating if the file type is supported
 */
export const isFileTypeSupported = (
  file: File,
  allowedTypes: readonly string[] = SUPPORTED_MIME_TYPES
): boolean => {
  const f = normalizeMarkdownType(file)
  return allowedTypes.some((type) => {
    if (type.endsWith("/*")) {
      const category = type.split("/")[0]
      return f.type.startsWith(`${category}/`)
    }
    return f.type === type
  })
}

/**
 * Checks if a file is a duplicate of existing files
 * @param file The file to check
 * @param existingFiles Array of existing files to check against
 * @returns boolean indicating if the file is a duplicate
 */
export const isFileDuplicate = (
  file: File,
  existingFiles: UploadingFile[]
): boolean => {
  return existingFiles.some(
    (badge) =>
      badge.file.name === file.name &&
      badge.file.size === file.size &&
      badge.file.lastModified === file.lastModified
  )
}

/**
 * Checks if a file exceeds the maximum allowed size
 * @param file The file to check
 * @param maxSizeMB Maximum size in megabytes (default: MAX_FILE_SIZE_MB)
 * @returns boolean indicating if the file exceeds the size limit
 */
export const isFileTooLarge = (
  file: File,
  maxSizeMB: number = MAX_FILE_SIZE_MB
): boolean => {
  return file.size > maxSizeMB * 1024 * 1024
}

/**
 * Filters files into supported and unsupported based on file type
 * @param files Array of files to filter
 * @returns Object with arrays of supported and unsupported files
 */
export const filterSupportedFiles = (
  files: File[],
  allowedTypes: readonly string[] = SUPPORTED_MIME_TYPES
): {
  supportedFiles: File[]
  unsupportedFiles: File[]
} => {
  const supported: File[] = []
  const unsupported: File[] = []

  files.forEach((file) => {
    const normalized = normalizeMarkdownType(file)
    if (isFileTypeSupported(normalized, allowedTypes)) {
      supported.push(normalized)
    } else {
      unsupported.push(file)
    }
  })

  return {
    supportedFiles: supported,
    unsupportedFiles: unsupported,
  }
}

/**
 * Filters files into supported, unsupported, duplicates, and oversized
 * @param files Array of files to filter
 * @param existingFiles Array of existing files to check for duplicates
 * @param maxSizeMB Maximum file size in MB (default: MAX_FILE_SIZE_MB)
 * @returns Object with arrays of valid, unsupported, duplicate, and oversized files
 */
export const validateFiles = (
  files: File[],
  existingFiles: UploadingFile[] = [],
  maxSizeMB: number = MAX_FILE_SIZE_MB,
  allowedTypes: readonly string[] = SUPPORTED_MIME_TYPES
): {
  validFiles: File[]
  unsupportedFiles: File[]
  duplicateFiles: File[]
  oversizedFiles: File[]
} => {
  const valid: File[] = []
  const unsupported: File[] = []
  const duplicates: File[] = []
  const oversized: File[] = []

  files.forEach((file) => {
    const normalized = normalizeMarkdownType(file)
    if (isFileTooLarge(file, maxSizeMB)) {
      oversized.push(file)
    } else if (!isFileTypeSupported(normalized, allowedTypes)) {
      unsupported.push(file)
    } else if (isFileDuplicate(normalized, existingFiles)) {
      duplicates.push(file)
    } else {
      valid.push(normalized)
    }
  })

  return {
    validFiles: valid,
    unsupportedFiles: unsupported,
    duplicateFiles: duplicates,
    oversizedFiles: oversized,
  }
}
